import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import AvatarPicker from 'components/@common/Avatar/AvatarPicker';
import { ModalContainer, ModalContent, ModalFooter, ModalHeader } from 'components/@common/Modal';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Formik, FormikProps, FormikValues } from 'formik';
import * as Yup from 'yup';
import { MutationFunctionOptions } from '@apollo/client';
import { ExecutionResult } from 'graphql';
import ProfileDetailsForm from './EditProfileDetails';
import { User } from 'types/schema-generated-types';
import { TFunction } from 'i18next';
import { VoidCallback } from 'types';

type EditProfileModalProps = {
  onClose: VoidCallback;
  onSubmitError?: VoidCallback;
  onSubmitSuccess?: VoidCallback;
  updateMe: (
    options?: MutationFunctionOptions<any, Record<string, any>> | undefined
  ) => Promise<ExecutionResult<any>>;
} & Partial<User>;

const useStyles = makeStyles((theme: Theme) => ({
  formContainer: {},
  selectorLabel: {
    marginBottom: theme.spacing(2)
  }
}));

const EditProfileModal = ({
  onClose,
  onSubmitSuccess,
  firstName,
  lastName,
  pin: oldPin,
  updateMe
}: EditProfileModalProps): ReactElement => {
  const classes = useStyles();
  const { t } = useTranslation('EditProfileModal');

  const handleFormikSubmit = async (
    values: FormikValues,
    { setFieldError }: any
  ): Promise<void> => {
    try {
      const pinValue = values.pin || values.oldPin;
      await updateMe({
        variables: {
          data: {
            firstName: values.firstName,
            lastName: values.lastName,
            pin: pinValue.toUpperCase()
          }
        }
      });
      if (onSubmitSuccess) onSubmitSuccess();
    } catch (error) {
      setFieldError('pin', t('PIN already in use. Please try another PIN.'));
    }
  };

  const validateForm = (t: TFunction) => (values: FormikValues): FormikValues => {
    const errors: FormikValues = {};
    if (values.pin.toUpperCase() === `${values.oldPin}`.toUpperCase()) {
      errors.pin = t('PIN already in use. Please try another PIN.');
    }
    if (values.pin !== '' && (values.pin?.length < 6 || values.pin?.length > 24)) {
      errors.pin = t('PIN should be 6-24 characters long.');
    }
    if (values.pin !== '' && !/^[A-Za-z0-9]+$/i.test(values.pin)) {
      errors.pin = t('The new PIN should only include letters or numbers.');
    }
    return errors;
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        firstName,
        lastName,
        oldPin,
        pin: ''
      }}
      validationSchema={Yup.object({
        firstName: Yup.string()
          .required()
          .min(2),
        lastName: Yup.string()
          .required()
          .min(2)
      })}
      validateOnChange={true}
      validateOnMount={true}
      validate={validateForm(t)}
      onSubmit={handleFormikSubmit}
    >
      {({ isSubmitting, isValid, dirty, submitForm }: FormikProps<FormikValues>): ReactElement => (
        <ModalContainer
          Header={<ModalHeader title={t('Edit Profile Information')} />}
          Footer={
            <ModalFooter
              onCancel={onClose}
              onSubmit={submitForm}
              submitDisabled={!dirty || !isValid || isSubmitting}
            />
          }
        >
          <ModalContent>
            <AvatarPicker />
            <ProfileDetailsForm classes={classes} />
          </ModalContent>
        </ModalContainer>
      )}
    </Formik>
  );
};

export default EditProfileModal;
